import type { GuidePageId, SafeRouteId } from './safeRoutes';
import type { GuidanceContext, TourDefinition, TourStep } from './types';

/**
 * Who sees which guide, and which of its steps — computed from the context
 * `GuidanceRoot` builds out of the SAME permission and effective-authority
 * hooks the UI uses. Pure and synchronous: no request is ever made to decide
 * visibility, and none of this is authority — the server decides what anybody
 * may do.
 */

/** A predicate that throws answers "no": a guide never breaks the page it is on. */
export function holds(predicate: (ctx: GuidanceContext) => boolean, ctx: GuidanceContext): boolean {
  try {
    return predicate(ctx) === true;
  } catch {
    return false;
  }
}

/** The steps this actor sees, in order. Hidden steps are not counted. */
export function visibleSteps(def: TourDefinition, ctx: GuidanceContext): TourStep[] {
  return def.steps.filter((step) => step.when === undefined || holds(step.when, ctx));
}

/** Whether a tour is offered at all: this portal, eligible, and something to show. */
export function isTourAvailable(def: TourDefinition, ctx: GuidanceContext): boolean {
  return def.portal === ctx.portal && holds(def.eligible, ctx) && visibleSteps(def, ctx).length > 0;
}

export function availableTours(
  catalogue: readonly TourDefinition[],
  ctx: GuidanceContext,
): TourDefinition[] {
  return catalogue.filter((def) => isTourAvailable(def, ctx));
}

/**
 * The page a step is shown on, where the engine may take the person: the
 * latest `route` at or before it in the definition, or `null` for a
 * shell-level step that works on any page. Read over ALL steps, so a hidden
 * routing step still fixes the page of the steps after it, and Back returns to
 * the right page.
 *
 * A record step (`needsRecord`) resets it (request w6-to-w1-1): it is on the
 * record the person opened themselves, the engine never goes there, and the
 * steps after it without a route of their own stay there too.
 */
export function stepRoute(def: TourDefinition, stepId: string): SafeRouteId | null {
  let route: SafeRouteId | null = null;

  for (const step of def.steps) {
    if (step.route !== undefined) route = step.route;
    else if (step.needsRecord !== undefined) route = null;

    if (step.id === stepId) return route;
  }

  return null;
}

/**
 * Whether a step belongs to a record the person opens: a record step
 * (`needsRecord`), or a step after one without a route of its own, which is
 * shown on that record's page (as `stepRoute` reads it). Read over ALL steps.
 */
export function isRecordBound(def: TourDefinition, stepId: string): boolean {
  let onRecord = false;

  for (const step of def.steps) {
    if (step.needsRecord !== undefined) onRecord = true;
    else if (step.route !== undefined) onRecord = false;

    if (step.id === stepId) return onRecord;
  }

  return false;
}

/**
 * The steps of an orientation — the first-visit "Show me around" (TOUR-02):
 * the steps this actor sees that need no record. It ends with Finish on the
 * portal's own pages instead of pausing for a record the person has not
 * opened. The full guide, from Help & tours, keeps its record steps.
 */
export function orientationSteps(def: TourDefinition, ctx: GuidanceContext): TourStep[] {
  return visibleSteps(def, ctx).filter((step) => !isRecordBound(def, step.id));
}

/** The first record step this actor sees: where an orientation leaves the full guide. */
export function firstRecordStep(def: TourDefinition, ctx: GuidanceContext): TourStep | undefined {
  return visibleSteps(def, ctx).find((step) => isRecordBound(def, step.id));
}

/**
 * The steps of "Guide me on this page": shell-level steps and those on this
 * page. Never one that would have to navigate, and never a record step for
 * another page.
 */
export function pageSteps(
  def: TourDefinition,
  ctx: GuidanceContext,
  page: GuidePageId,
): TourStep[] {
  return visibleSteps(def, ctx).filter((step) => {
    const route = stepRoute(def, step.id);

    if (route !== null && route !== page) return false;
    if (step.needsRecord !== undefined && step.needsRecord.routeId !== page) return false;

    return true;
  });
}

/**
 * Tours that offer "Guide me on this page" here: available, listing this page,
 * with steps on it — and fewer than the whole guide. Where this page's steps
 * ARE the whole guide (the tenant orientation on the dashboard), its Start
 * already does exactly that, and offering it twice is noise (R12-12).
 */
export function toursForPage(
  catalogue: readonly TourDefinition[],
  ctx: GuidanceContext,
  page: GuidePageId | null,
): TourDefinition[] {
  if (page === null) return [];

  return availableTours(catalogue, ctx).filter((def) => {
    if (!(def.pageRoutes as readonly GuidePageId[]).includes(page)) return false;

    const here = pageSteps(def, ctx, page);

    return here.length > 0 && here.length < visibleSteps(def, ctx).length;
  });
}
